import { PAGE_SIZE } from "./constants";
import { DailyLog, DailyLogAttachment, NewDailyLogInput } from "./types";
import { getUserId } from "@/lib/auth-session";
import { useEntityList } from "@/hooks/useEntityList";
import { getApiUrl } from '@/lib/api-config';
import { uploadEntityAttachments, fetchEntityAttachmentsByIds, toNamedAttachment, deleteAttachment } from '@/lib/entity-attachments';
import { useUser } from "@/contexts/UserContext";
import { useProject } from "@/components/ProjectSelector";

function mapRowToDailyLog(row: any): DailyLog {
  return {
    id: row.id,
    date: row.date,
    createdAt: row.created_at,
    projectId: row.project_id,
    notes: row.notes ?? undefined,
    issues: row.issues ?? undefined,
    attachments: [],
    weatherMode: row.weather_mode as DailyLog["weatherMode"],
    weatherText: row.weather_text ?? undefined,
    weatherConditionCode: row.weather_condition_code ?? undefined,
    temperatureHighCelsius: row.temperature_high_celsius ?? undefined,
    temperatureLowCelsius: row.temperature_low_celsius ?? undefined,
    windKph: row.wind_kph ?? undefined,
    humidityPercent: row.humidity_percent ?? undefined,
    precipitationMm: row.precipitation_mm ?? undefined,
    visibleToClient: row.visible_to_client === true,
    author: row.created_by_name || 'Unknown User',
  };
}

const toDailyLogAttachment = toNamedAttachment;

async function fetchAttachmentsForLogIds(logIds: string[]): Promise<Map<string, DailyLogAttachment[]>> {
  const grouped = await fetchEntityAttachmentsByIds('daily_log', logIds);
  return new Map(Array.from(grouped, ([id, list]) => [id, list.map(toNamedAttachment)]));
}

const toWeatherPayload = (input: Partial<NewDailyLogInput>) => ({
  ...(input.weatherMode !== undefined ? { weather_mode: input.weatherMode } : {}),
  ...(input.weatherText !== undefined ? { weather_text: input.weatherText ?? null } : {}),
  ...(input.windKph !== undefined ? { wind_kph: input.windKph ?? null } : {}),
  ...(input.humidityPercent !== undefined ? { humidity_percent: input.humidityPercent ?? null } : {}),
  ...(input.precipitationMm !== undefined ? { precipitation_mm: input.precipitationMm ?? null } : {}),
  ...(input.visibleToClient !== undefined ? { visible_to_client: input.visibleToClient === true } : {}),
});

/** Creates a daily log through the API (the backend derives company and author). */
export async function createDailyLog(input: NewDailyLogInput, author: string): Promise<DailyLog | undefined> {
  const payload = {
    date: input.date,
    project_id: input.projectId,
    notes: input.notes ?? null,
    issues: input.issues ?? null,
    ...toWeatherPayload(input),
    weather_condition_code: null,
    temperature_high_celsius: null,
    temperature_low_celsius: null,
  };
  const response = await fetch(getApiUrl('api/entities/daily_log'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    console.error('Failed to create daily log', await response.json());
    return undefined;
  }
  const result = await response.json();
  return { ...mapRowToDailyLog(result.data), attachments: input.attachments ?? [], author } as DailyLog;
}

/** Updates a daily log through the API; the row comes back as the server saved it. */
export async function updateDailyLogRecord(id: string, updates: Partial<NewDailyLogInput>): Promise<DailyLog | undefined> {
  const payload: Record<string, unknown> = {
    ...(updates.date !== undefined ? { date: updates.date } : {}),
    ...(updates.projectId !== undefined ? { project_id: updates.projectId } : {}),
    ...(updates.notes !== undefined ? { notes: updates.notes ?? null } : {}),
    ...(updates.issues !== undefined ? { issues: updates.issues ?? null } : {}),
    ...toWeatherPayload(updates),
  };
  const response = await fetch(getApiUrl(`api/entities/daily_log/${id}`), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    console.error('Failed to update daily log', await response.json());
    return undefined;
  }
  return mapRowToDailyLog((await response.json()).data);
}

/** Uploads files to a daily log (the server re-derives company and project and rejects a mismatching path). */
export async function uploadDailyLogFiles(dailyLogId: string, files: File[], companyId: string): Promise<DailyLogAttachment[]> {
  const sessionUserId = getUserId() ?? undefined;
  if (!sessionUserId || !dailyLogId || files.length === 0 || !companyId) return [];
  return (await uploadEntityAttachments('daily_log', dailyLogId, files, companyId, sessionUserId)).map(toDailyLogAttachment);
}

export async function deleteDailyLogFile(attachmentId: string): Promise<boolean> {
  return deleteAttachment(attachmentId, getUserId() ?? undefined);
}

/**
 * The Daily Logs list: the generic server-driven list (hooks/useEntityList.ts) with this module's row mapping and
 * per-page files. Search (notes, issues, date, project, author), paging and the project filter run in the database
 * over every daily log the user may see -- searching never hides a log that is on a later page.
 */
export function useDailyLogs() {
  const { companyId } = useUser();
  const { selectedProject } = useProject();
  const list = useEntityList<any, DailyLog>({
    entityType: 'daily_log',
    projectId: selectedProject,
    enabled: !!companyId,
    mode: 'append',
    pageSize: PAGE_SIZE,
    includeCreator: true,
    mapRow: mapRowToDailyLog,
    enrichPage: async (rows, items) => {
      const attachmentsById = await fetchAttachmentsForLogIds(rows.map((r) => r.id));
      return items.map((item) => ({ ...item, attachments: attachmentsById.get(item.id) ?? [] }));
    },
  });
  const { setItems, reload } = list;

  return {
    ...list,
    /** A new log: where it belongs depends on the server's order / search, so the first page is read again. */
    async addDailyLog(input: NewDailyLogInput, author: string): Promise<DailyLog | undefined> {
      const created = await createDailyLog(input, author);
      if (created) reload();
      return created;
    },
    async updateDailyLog(id: string, updates: Partial<NewDailyLogInput>): Promise<DailyLog | undefined> {
      const mapped = await updateDailyLogRecord(id, updates);
      if (mapped) {
        // Attachments updated client-side but not persisted stay local; the displayed author does not change.
        setItems((prev) => prev.map((log) => (log.id === id ? { ...mapped, attachments: updates.attachments ?? log.attachments, author: log.author } : log)));
      }
      return mapped;
    },
  };
}

/**
 * Standalone function to upload attachments to a daily log
 * Similar to uploadProposalAttachments in proposals store
 */
export async function uploadDailyLogAttachments(
  dailyLogId: string,
  files: File[],
  companyId: string,
  userId: string
): Promise<DailyLogAttachment[]> {
  return (await uploadEntityAttachments('daily_log', dailyLogId, files, companyId, userId)).map(toDailyLogAttachment);
}
